import { PaginatedList, PaginationParams } from "../types/pagination";
import { unwrapApiResponse } from "../utils/apiUtils";
import { api } from "./apiSlice";

/**
 * Tenant interface matching TenantDto from backend
 */
export interface Tenant {
  id: number;
  name: string;
  contactEmail?: string;
  contactPhone?: string;
  safetyEmail?: string;
  isActive: boolean;
  stripeCustomerId?: string | null;
  createdAt: string;
  updatedAt?: string;
  currentSubscriptionId?: number | null;
  subscriptionStatus?: string | null;
  planName?: string | null;
  planCode?: string | null;
  subscriptionStartDate?: string | null;
  subscriptionEndDate?: string | null;
  trialEndDate?: string | null;
  currentPeriodEnd?: string | null;
  isTrialing?: boolean;
}

/**
 * Create tenant DTO matching CreateTenantDto from backend
 */
export interface CreateTenantDto {
  name: string;
  contactEmail?: string;
  contactPhone?: string;
  safetyEmail?: string;
}

/**
 * Update tenant DTO matching UpdateTenantDto from backend
 */
export interface UpdateTenantDto {
  name: string;
  contactEmail?: string;
  contactPhone?: string;
  safetyEmail?: string;
  isActive: boolean;
}

export const tenantsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTenantsPaginated: builder.query<PaginatedList<Tenant>, PaginationParams>({
      query: (params) => ({
        url: "tenants",
        params: params,
      }),
      providesTags: ["TenantsList"],
      transformResponse: unwrapApiResponse,
    }),
    getAllTenants: builder.query<Tenant[], void>({
      query: () => "tenants",
      providesTags: ["TenantsList"],
      transformResponse: unwrapApiResponse,
    }),
    getTenantById: builder.query<Tenant, number>({
      query: (id) => `tenants/${id}`,
      providesTags: (result, error, id) => [{ type: "Tenant", id }],
      transformResponse: unwrapApiResponse,
    }),
    createTenant: builder.mutation<Tenant, CreateTenantDto>({
      query: (tenant) => ({
        url: "tenants",
        method: "POST",
        body: tenant,
      }),
      invalidatesTags: ["TenantsList"],
      transformResponse: unwrapApiResponse,
    }),
    updateTenant: builder.mutation<Tenant, { id: number; tenant: UpdateTenantDto }>({
      query: ({ id, tenant }) => ({
        url: `tenants/${id}`,
        method: "PUT",
        body: tenant,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Tenant", id },
        "TenantsList",
        "Tenant",
      ],
      transformResponse: unwrapApiResponse,
    }),
    deleteTenant: builder.mutation<void, number>({
      query: (id) => ({
        url: `tenants/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["TenantsList"],
    }),
  }),
});

export const {
  useGetTenantsPaginatedQuery,
  useGetAllTenantsQuery,
  useGetTenantByIdQuery,
  useCreateTenantMutation,
  useUpdateTenantMutation,
  useDeleteTenantMutation,
} = tenantsApi;
